/**
 * Remote player replication: interpolate between the last two `snapshot` frames.
 *
 * Policy: aim ~150 ms behind the newest snapshot (`INTERPOLATION_DELAY_MS`), interpolate between the
 * two buffered snapshots that bracket the render tick, and FREEZE (never extrapolate) when the render
 * tick falls outside the buffer - extrapolating a 10 cm-voxel player into terrain is far worse than
 * showing them a hair late. The local player is never driven from these samples: it is rendered from
 * local prediction, and `localId` is skipped here.
 *
 * The map/array work is done once per snapshot pair, not per frame, and every per-frame struct is
 * reused, so the render path allocates nothing.
 */

import type { PlayerState, Vec3 } from "@rogueworlds/shared";
import type { PlayerRenderer } from "../render/players";

interface Snapshot {
  readonly tick: number;
  readonly players: readonly PlayerState[];
  readonly receivedMs: number;
}

const BUFFER_LIMIT = 32;
const TWO_PI = Math.PI * 2;
/** ~150 ms of artificial delay, per the runtime contract. */
export const INTERPOLATION_DELAY_MS = 150;

export class RemotePlayers {
  private readonly buffer: Snapshot[] = [];
  private readonly renderer: Pick<PlayerRenderer, "upsert" | "remove">;
  private readonly tickHz: number;
  private readonly rendered = new Set<string>();
  private readonly scratch: Vec3 = { x: 0, y: 0, z: 0 };
  private olderIndex = new Map<string, PlayerState>();
  private pairKey = "";
  private localId = "";

  constructor(renderer: Pick<PlayerRenderer, "upsert" | "remove">, tickHz: number) {
    this.renderer = renderer;
    this.tickHz = tickHz;
  }

  /** The local player's id, received with `welcome`; those samples are never rendered from here. */
  setLocalId(id: string): void {
    this.localId = id;
  }

  /** Buffers a `snapshot` frame. */
  push(tick: number, players: readonly PlayerState[], nowMs: number): void {
    this.buffer.push({ tick, players, receivedMs: nowMs });
    if (this.buffer.length > BUFFER_LIMIT) {
      this.buffer.splice(0, this.buffer.length - BUFFER_LIMIT);
    }
  }

  /** Interpolates the buffered snapshots onto `PlayerRenderer`. Call once per frame. */
  update(nowMs: number): void {
    const count = this.buffer.length;
    if (count === 0) {
      return;
    }
    const newest = this.buffer[count - 1];
    if (newest === undefined) {
      return;
    }
    const delayTicks = (INTERPOLATION_DELAY_MS / 1000) * this.tickHz;
    const aheadS = (nowMs - newest.receivedMs) / 1000;
    const renderTick = newest.tick + aheadS * this.tickHz - delayTicks;

    let older = this.buffer[0];
    let newer = newest;
    if (older === undefined) {
      return;
    }
    if (renderTick <= older.tick) {
      newer = older; // frozen at the oldest sample we hold
    } else if (renderTick >= newest.tick) {
      older = newest; // frozen at the newest sample: never extrapolate
    } else {
      for (let i = count - 1; i > 0; i--) {
        const current = this.buffer[i];
        const previous = this.buffer[i - 1];
        if (current === undefined || previous === undefined) {
          continue;
        }
        if (previous.tick <= renderTick && current.tick >= renderTick) {
          older = previous;
          newer = current;
          break;
        }
      }
    }

    const span = newer.tick - older.tick;
    const alpha = span > 0 ? clamp01((renderTick - older.tick) / span) : 1;

    const key = `${older.tick}:${newer.tick}`;
    if (key !== this.pairKey) {
      this.pairKey = key;
      this.olderIndex.clear();
      for (const player of older.players) {
        this.olderIndex.set(player.id, player);
      }
    }

    const seen = this.rendered;
    const previousIds: string[] = [];
    for (const id of seen) {
      previousIds.push(id);
    }
    seen.clear();

    for (const player of newer.players) {
      if (player.id === this.localId) {
        continue;
      }
      const before = this.olderIndex.get(player.id);
      const pos = this.scratch;
      if (before === undefined || span === 0) {
        pos.x = player.pos.x;
        pos.y = player.pos.y;
        pos.z = player.pos.z;
      } else {
        pos.x = before.pos.x + (player.pos.x - before.pos.x) * alpha;
        pos.y = before.pos.y + (player.pos.y - before.pos.y) * alpha;
        pos.z = before.pos.z + (player.pos.z - before.pos.z) * alpha;
      }
      const yaw = before === undefined ? player.yaw : lerpAngle(before.yaw, player.yaw, alpha);
      this.renderer.upsert(player.id, pos, yaw, false);
      seen.add(player.id);
    }

    for (const id of previousIds) {
      if (!seen.has(id)) {
        this.renderer.remove(id);
      }
    }
  }

  clear(): void {
    this.buffer.length = 0;
    this.olderIndex.clear();
    this.pairKey = "";
    for (const id of this.rendered) {
      this.renderer.remove(id);
    }
    this.rendered.clear();
  }

  get count(): number {
    return this.rendered.size;
  }

  /** Latest buffered server tick (HUD). */
  get latestTick(): number {
    const newest = this.buffer[this.buffer.length - 1];
    return newest === undefined ? 0 : newest.tick;
  }

  /** Players reported by the newest buffered snapshot (HUD). */
  get latestPlayers(): number {
    const newest = this.buffer[this.buffer.length - 1];
    return newest === undefined ? 0 : newest.players.length;
  }
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Shortest-path angle interpolation; never uses `Math.sin/cos`. */
function lerpAngle(a: number, b: number, t: number): number {
  const delta = (((b - a + Math.PI) % TWO_PI) + TWO_PI) % TWO_PI - Math.PI;
  return a + delta * t;
}
